
import { matchLeft } from "../Utils";
import { httpGet, httpPost } from "../Web";
import { ApiResponse } from "./ApiResponse";
import { getCompanies } from "./Company";
import { Interface, enumerateInterfaces } from "./Interface";
import { Product, getProducts } from "./Product";
import { WorkshopProductInterface } from "./WorkshopProductInterface";
import { WorkshopProductCombination } from "./WorkshopProductCombination";

export interface WorkshopProduct {
    id: number,
    productId: number,
    workshopId: number,
    status: WorkshopProductStatus,
    productVersion?: string | null,
    releaseStatus: ProductReleaseStatus,
    description?: string | null,
    
    isServer: boolean,
    isClient: boolean,
    clientReadyToTest: boolean,
    serverReadyToTest: boolean,
    guid?: string
}

export interface WorkshopProductFull {
    wsProduct: WorkshopProduct,
    product: Product,
}

export interface WorkshopProductWIthInterface {
    wsProduct: WorkshopProduct,
    product: Product,
    interfaces: WorkshopProductInterface[],
    combinations: WorkshopProductCombination[]
}

export interface UnsupportedTestCase {
    id?: number,
    workshopProductId: number,
    testCaseId: number
}

export interface WorkshopProductImportSelection {
    workshopIdTo: number,
    workshopProductIdFrom: number,
    combinations: WorkshopProductCombination[],
    interfaces: WorkshopProductInterface[]
}

export enum WorkshopProductStatus { Unknown = 0, Offline = 1, Available = 2 };

export const WorkshopProductStatusStrings = ["Unknown", "Offline", "Available"];

export enum ProductReleaseStatus { Unknown = 0, Development = 1, ReleaseCandidate = 2, Released = 3 };

export const ProductReleaseStatusStrings = ["Unknown", "Development", "Release Candidate", "Released"];

export const getWorkshopProducts = (workshopId: number, abortController: AbortController): Promise<ApiResponse<WorkshopProduct[]>> =>
    httpGet(`/api/workshopproduct/${workshopId}`, abortController);

export const getWorkshopProductsFull = (workshopId: number, abortController: AbortController): Promise<ApiResponse<WorkshopProductFull[]>> =>
    httpGet(`/api/workshopproduct/workshopproductfull/${workshopId}`, abortController);

export const getWorkshopProductsWithInterface = (workshopId: number, abortController: AbortController): Promise<ApiResponse<WorkshopProductWIthInterface[]>> =>
    httpGet(`/api/workshopproduct/workshopproductswithinterface/${workshopId}`, abortController);

export const getSingleWorkshopProduct = (id: number, abortController: AbortController): Promise<ApiResponse<WorkshopProduct>> =>
    httpGet(`/api/workshopproduct/getwp/${id}`, abortController);

export const getCompanyWorkshopProducts = async (companyName: string, workshopId: number, abortController: AbortController): Promise<WorkshopProduct[]> => {
    try {
        const companiesResponse = await getCompanies(abortController);
        const workshopProductsResponse = await getWorkshopProducts(workshopId, abortController);
        
        if(companiesResponse.failed)
            return Promise.reject(companiesResponse.errorText);
        if(workshopProductsResponse.failed)
            return Promise.reject(workshopProductsResponse.errorText);
        
        const company = companiesResponse.result.find(company => company.name === companyName);
        const productsResponse = await getProducts(abortController);

        if(productsResponse.failed)
            return Promise.reject(productsResponse.failed);

        const companyProducts = productsResponse.result.filter(product => product.companyId === company?.id);

        return Promise.resolve(workshopProductsResponse.result.filter(wp => companyProducts.find(cp => cp.id === wp.productId)));
    } catch(error) {
        return Promise.reject(error);
    }
}

export const deleteWorkshopProduct = (id: number, abortController: AbortController): Promise<ApiResponse<WorkshopProduct>> =>
    httpGet(`/api/workshopproduct/delete/${id}`, abortController);

export const updateWorkshopProduct = (workshopProduct: WorkshopProduct, abortController: AbortController): Promise<ApiResponse<WorkshopProduct>> =>
    httpPost('/api/workshopproduct/update/', workshopProduct, abortController);

export const getWorkshopProductInterfaces = (workshopProductId: number, abortController: AbortController): Promise<ApiResponse<WorkshopProductInterface[]>> =>
    httpGet(`/api/workshopproduct/interface/${workshopProductId}`, abortController);

export const updateWorkshopProductInterface = (workshopProductInterface: WorkshopProductInterface, abortController: AbortController) =>
    httpPost('/api/workshopproduct/interface/update/', workshopProductInterface, abortController);

export const deleteWorkshopProductInterface = (workshopProductInterface: WorkshopProductInterface, abortController: AbortController) =>
    httpPost('/api/workshopproduct/interface/delete', workshopProductInterface, abortController);

export const getInterfaces = async (workshopProductId: number, abortController: AbortController): Promise<ApiResponse<Interface[]>> => {
    try {
        const wpiResponse = await getWorkshopProductInterfaces(workshopProductId, abortController);
        const ifaceResponse = await enumerateInterfaces(abortController);

        if(!wpiResponse.result || !ifaceResponse.result) {}
        
        return {failed: false, errorCode: null,
                errorText: null, result: matchLeft(wpiResponse.result, ifaceResponse.result, (wpi, iface) => wpi.interfaceId === iface.id).flatMap(([wpi, iface])=> iface) }
    } catch(error) {
        return Promise.reject();
    }
}

export const enumerateUnsupportedTestCases = (workshopProductId: number, abortController: AbortController): Promise<ApiResponse<UnsupportedTestCase[]>> =>
    httpGet(`/api/workshopproduct/unsupported/${workshopProductId}`, abortController);

export const deleteUnsupportedTestCase = (unsupportedTestCase: UnsupportedTestCase, abortController: AbortController): Promise<ApiResponse<UnsupportedTestCase>> =>
    httpGet(`/api/workshopproduct/unsupported/delete/${unsupportedTestCase.id}`, abortController);

export const updateUnsupportedTestCase = (unsupportedTestCase: UnsupportedTestCase, abortController: AbortController): Promise<ApiResponse<UnsupportedTestCase>> =>
    httpPost('/api/workshopproduct/unsupported/update', unsupportedTestCase, abortController);

export const importWorkshopProductSettings = (workshopProductImportSelections: WorkshopProductImportSelection[], abortController: AbortController): Promise<ApiResponse<number[]>> =>
    httpPost('/api/workshopproduct/settings/importselection', workshopProductImportSelections, abortController);